"use client";

import { ChevronLeft } from "lucide-react";
import * as React from "react";
import { trackMarketingDemoBooked, trackMarketingFormSubmitted } from "../analytics";
import { CalEmbed } from "../cal-embed";
import { type Brand, BRANDS } from "../demo-brands";

/**
 * Book-a-demo scheduler: first asks which brand type the visitor is, then
 * shows the matching cal.com scheduler (boutique books a different event than
 * multi-location/franchise).
 */
export function DemoScheduler() {
  const [brand, setBrand] = React.useState<Brand | null>(null);

  function chooseBrand(next: Brand) {
    setBrand(next);
    trackMarketingFormSubmitted("demo", { brandType: next.id });
  }

  if (brand) {
    return (
      <div>
        <div className="mx-auto max-w-[700px]">
          <button
            type="button"
            onClick={() => setBrand(null)}
            className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
            {brand.label}
          </button>
        </div>
        <div className="overflow-hidden">
          <CalEmbed
            calLink={brand.calLink}
            onBookingSuccess={() => trackMarketingDemoBooked(brand.id)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-border bg-background p-6 shadow-sm sm:p-10">
      <div className="text-center">
        <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
          First, which best describes your brand?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          We&apos;ll match you with the right team and tailor the walkthrough.
        </p>
      </div>

      <div className="mt-7 grid gap-3 sm:grid-cols-3">
        {BRANDS.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={() => chooseBrand(b)}
            className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-background p-5 text-center transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:hover:border-brand-accent/40 dark:focus-visible:ring-brand-accent/50"
          >
            <span className="flex size-12 items-center justify-center rounded-xl border border-border bg-muted/50 text-muted-foreground transition-colors group-hover:border-primary/30 group-hover:bg-primary/10 group-hover:text-primary dark:group-hover:border-brand-accent/30 dark:group-hover:bg-brand-accent/10 dark:group-hover:text-brand-accent">
              <b.icon className="size-5" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-foreground">{b.label}</span>
              <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                {b.blurb}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
